import { useState } from "react";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";

const Budget = () => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  interface FormData {
    name: string;
    phone: string;
    email: string;
    service: string;
    description?: string;
  }

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);

    const GOOGLE_SCRIPT_URL_BUDGET = import.meta.env.VITE_GOOGLE_SCRIPT_URL;

    if (!GOOGLE_SCRIPT_URL_BUDGET) {
      console.error("A URL do Google Script não está definida no .env");
      toast.error(
        "Ocorreu um erro na configuração. Tente novamente mais tarde."
      );
      setIsSubmitting(false);
      return;
    }

    const sheetData = {
      "Data/Hora": new Date().toLocaleString("pt-BR"),
      Origem: "Site - Orçamento",
      Nome: data.name,
      Telefone: data.phone,
      Email: data.email,
      Serviço: data.service,
      Mensagem: data.description || "Não informado.",
      "Tornou cliente": "Aguardando",
    };

    try {
      await fetch(GOOGLE_SCRIPT_URL_BUDGET, {
        method: "POST",
        mode: "no-cors",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(sheetData),
      });

      toast.success(
        "Solicitação enviada com sucesso! Entraremos em contato em breve."
      );
      reset();
    } catch (error) {
      console.error("Erro ao enviar dados para o Google Sheets:", error);
      toast.error("Houve um erro ao enviar sua solicitação. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section 
        className="py-20 bg-gradient-to-r from-blue-900 to-cyan-800 text-white"
        aria-labelledby="budget-hero-title"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 id="budget-hero-title" className="text-5xl md:text-6xl font-bold mb-6">
              Orçamentos e Serviços
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto">
              Solicite seu orçamento sem compromisso e descubra como a Tech
              Glacial pode transformar seu ambiente com soluções de refrigeração
              e climatização personalizadas.
            </p>
          </motion.div>
        </div>
      </section>

      <section 
        className="py-20 bg-white"
        aria-labelledby="budget-form-title"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 text-gray-900">
            <h2 id="budget-form-title" className="text-3xl font-bold mb-4">
              Solicite um Orçamento
            </h2>
            <p className="text-lg">
              Preencha o formulário e receba uma proposta personalizada
            </p>
          </div>

          <motion.form
            onSubmit={handleSubmit(onSubmit)}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-gray-100 bg-opacity-800/80 backdrop-blur-md p-8 rounded-2xl shadow-lg text-gray-900"
            aria-label="Formulário de solicitação de orçamento"
            noValidate
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium mb-2">
                  Nome Completo <span className="text-red-600" aria-label="obrigatório">*</span>
                </label>
                <input
                  id="name"
                  type="text"
                  {...register("name", { required: "Nome é obrigatório" })}
                  className="w-full px-4 py-3 rounded-lg bg-gray-700 border border-gray-600 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 transition"
                  placeholder="Seu nome completo"
                  aria-required="true"
                  aria-invalid={errors.name ? "true" : "false"}
                  aria-describedby={errors.name ? "name-error" : undefined}
                />
                {errors.name && (
                  <p id="name-error" className="text-red-400 text-sm mt-1" role="alert">
                    {String(errors.name.message)}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium mb-2">
                  Telefone <span className="text-red-600" aria-label="obrigatório">*</span>
                </label>
                <input
                  id="phone"
                  type="tel"
                  {...register("phone", { required: "Telefone é obrigatório" })}
                  className="w-full px-4 py-3 rounded-lg bg-gray-700 border border-gray-600 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 transition"
                  placeholder="(11) 99999-9999"
                  aria-required="true"
                  aria-invalid={errors.phone ? "true" : "false"}
                  aria-describedby={errors.phone ? "phone-error" : undefined}
                />
                {errors.phone && (
                  <p id="phone-error" className="text-red-400 text-sm mt-1" role="alert">
                    {String(errors.phone?.message)}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-2">
                  E-mail <span className="text-red-600" aria-label="obrigatório">*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  {...register("email", { 
                    required: "E-mail é obrigatório",
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: "E-mail inválido"
                    }
                  })}
                  className="w-full px-4 py-3 rounded-lg bg-gray-700 border border-gray-600 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 transition"
                  placeholder="seu@email.com"
                  aria-required="true"
                  aria-invalid={errors.email ? "true" : "false"}
                  aria-describedby={errors.email ? "email-error" : undefined}
                />
                {errors.email && (
                  <p id="email-error" className="text-red-400 text-sm mt-1" role="alert">
                    {String(errors.email?.message)}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="service" className="block text-sm font-medium mb-2">
                  Tipo de Serviço <span className="text-red-600" aria-label="obrigatório">*</span>
                </label>
                <select
                  id="service"
                  {...register("service", { required: "Selecione um serviço" })}
                  className="w-full px-4 py-3 rounded-lg bg-gray-700 border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 transition"
                  aria-required="true"
                  aria-invalid={errors.service ? "true" : "false"}
                  aria-describedby={errors.service ? "service-error" : undefined}
                  defaultValue=""
                >
                  <option value="" disabled>
                    Selecione o serviço
                  </option>
                  <option value="ar-condicionado">Ar Condicionado</option>
                  <option value="refrigeracao">Refrigeração Industrial</option>
                  <option value="manutencao">Manutenção</option>
                  <option value="projeto">Projeto Personalizado</option>
                </select>
                {errors.service && (
                  <p id="service-error" className="text-red-400 text-sm mt-1" role="alert">
                    {String(errors.service.message)}
                  </p>
                )}
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="description" className="block text-sm font-medium mb-2">
                Descrição do Projeto <span className="text-gray-500">(opcional)</span>
              </label>
              <textarea
                id="description"
                {...register("description")}
                rows={4}
                className="w-full px-4 py-3 rounded-lg bg-gray-700 border border-gray-600 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 transition"
                placeholder="Descreva suas necessidades e detalhes do projeto..."
                aria-describedby="description-hint"
              ></textarea>
              <p id="description-hint" className="text-sm text-gray-500 mt-1">
                Quanto mais detalhes você fornecer, melhor poderemos atendê-lo
              </p>
            </div>

            <div 
              role="status" 
              aria-live="polite" 
              className="sr-only"
            >
              {isSubmitting ? "Enviando solicitação..." : ""}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              aria-busy={isSubmitting}
              className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 hover:shadow-xl hover:shadow-gray-500/50 disabled:bg-gray-500 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2"
            >
              {isSubmitting ? "Enviando..." : "Solicitar Orçamento Gratuito"}
            </button>

            <p className="text-sm text-gray-600 mt-4 text-center">
              <span className="text-red-600">*</span> Campos obrigatórios
            </p>
          </motion.form>
        </div>
      </section>
    </div>
  );
};

export default Budget;